import { CircleInfo } from "@/icons/CircleInfo.tsx";
import { MagnifyingGlass } from "@/icons/MagnifyingGlass.tsx";
import api from "@/lib/api.ts";
import { TagsResponse } from "@/models/tag.model.ts";
import { TemplateModel, TemplatesResponse } from "@/models/template.model.ts";
import { Error } from "@/ui/shared/Error.tsx";
import { Loading } from "@/ui/shared/Loading.tsx";
import { TemplateCard } from "@/ui/shared/TemplateCard.tsx";
import {
  Alert,
  Chip,
  CloseButton,
  Input,
  Pagination,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export default function Explore() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [page, setPage] = useState<number>(1);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [debouncedPage] = useDebouncedValue(page, 400);
  const [templates, setTemplates] = useState<TemplateModel[]>();

  const templatesParams = {
    page: debouncedPage,
    limit: 50,
    sort: "-created_at",
    "filter[search]": debouncedSearch,
    "filter[tags]": selectedTags.join(","),
    include: "tags",
  };

  const { status: templatesStatus, data: templatesData } = useQuery({
    queryKey: ["templates", templatesParams],
    queryFn: () =>
      api.get<TemplatesResponse>("templates?include=tags", {
        params: templatesParams,
      }),
    retry: 1,
  });
  const templatesResponse: TemplatesResponse | undefined = templatesData?.data;

  useEffect(() => {
    if (templatesData) {
      const templatesResponse: TemplatesResponse = templatesData?.data || [];
      setTemplates(templatesResponse.data);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [templatesData]);

  if (templatesStatus === "error") {
    return <Error />;
  }

  const tagsParams = {
    limit: 500,
    sort: "name",
  };

  const { data: tagsData } = useQuery({
    queryKey: ["tags", tagsParams],
    queryFn: () =>
      api.get<TagsResponse>("tags", {
        params: tagsParams,
      }),
    retry: 1,
  });
  const tagsResponse: TagsResponse | undefined = tagsData?.data;
  const tags: string[] = tagsResponse?.data.map((t) => t.name) || [];

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <div className="col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-3 2xl:col-span-4 flex flex-col gap-4">
      <div className="flex flex-col md:flex-row gap-2 w-full justify-start md:justify-between md:items-center">
        <Title order={3} fw={500}>
          Explore
        </Title>

        <TextInput
          miw={300}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          leftSection={<MagnifyingGlass size={14} />}
          placeholder="Search photos by prompt..."
          rightSection={
            search && (
              <CloseButton
                className="cursor-pointer"
                aria-label="Clear input"
                onClick={() => setSearch("")}
              />
            )
          }
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {tags?.map((tag) => (
          <Chip
            size="xs"
            key={tag}
            checked={selectedTags.includes(tag)}
            onChange={() => toggleTag(tag)}
          >
            {tag}
          </Chip>
        ))}
      </div>

      {templatesStatus === "pending" && (
        <div className="flex flex-col w-full justify-center mb-4">
          <Loading />
        </div>
      )}

      {templates?.length === 0 && templatesStatus === "success" && (
        <Alert
          bd="1px dashed"
          color="grape"
          icon={<CircleInfo size={20} />}
          className="w-full"
        >
          No photos found!
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
        {templates?.map((template) => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </div>

      {templatesResponse && templatesResponse.meta.total > 0 && (
        <div className="flex items-center justify-between gap-2">
          <Pagination
            value={page}
            onChange={setPage}
            total={templatesResponse.meta.last_page}
            radius="md"
            withEdges
          />

          <div className="flex flex-row flex-nowrap gap-2 items-center">
            <Text>Page:</Text>
            <Input
              w={100}
              className="text-center"
              type="number"
              value={page}
              onChange={(event) => setPage(+event.currentTarget.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
